// ============================================================
// SESSION FILE v4 - roster export/import.
//
// v3 and earlier describe a session as `{ atkProfiles, tabs }`: two
// uncoupled halves of the same people. v4 exports the roster itself,
// which is what makes "export Brother Castiel and get Brother Castiel
// back" true rather than "get his guns and, separately, his stat block".
//
// The sheet/state split gives export a choice it never had before:
//
//   sheet only  - a clean template. Full wounds, no conditions, full
//                 magazines. What you hand another GM.
//   sheet+state - the fight exactly as it stands, mid-battle.
//
// Reading is deliberately tolerant (this is untrusted file input) and
// v1–v3 files are NOT handled here - they keep flowing through the
// legacy tabs/atkProfiles path in useImportExport, which already runs
// them through migrateSessionV1toV2.
// ============================================================
import type { Character } from '../character/types';
import { normalizeCharacter } from '../character/normalize';
import { emptyCharacter } from '../character/factories';
import { emptyCombatState, withoutStanding } from './factories';
import { sanitizeSlotOrder } from './serialize';
import type { Combatant, CombatState, DefenderSheet, Loadout, LoadoutRef } from './types';

export const SESSION_FILE_VERSION = 4;

export interface CombatantFileEntry {
  id: string;
  name: string;
  kind: string;
  order: number;
  sheet: Character;
  overrides: Partial<DefenderSheet>;
  /** Absent in a sheet-only export - import then starts them fresh. */
  state?: CombatState;
  /** Written dense; read tolerantly - a v4 file from an older client is sparse. */
  slotOrder?: (string | null)[] | null;
  loadoutRef?: LoadoutRef | null;
  narrativeLog?: unknown[];
}

export interface RosterExportOptions {
  /** false → a clean template: full wounds, no conditions, full mags. */
  includeState: boolean;
  includeNarrative: boolean;
  /**
   * false → the Protagonist rules are stripped from every sheet.
   *
   * Handing another GM your map is usual; handing them the reason one of
   * your marines cannot die is not. It also travels the other way: an
   * imported character carrying somebody else's plot armour would be
   * inert (the table ruling gates it) but still surprising to find.
   */
  includeProtagonist?: boolean;
}

/**
 * The sheet without its Protagonist rules - and unchanged, by identity,
 * when it never had any. `Character` is never mutated in place: the
 * projections are memoised on object identity, so an in-place edit would
 * be invisible to every panel.
 */
function stripProtagonist(sheet: Character): Character {
  if (!sheet?.protagonist) return sheet;
  const { protagonist: _dropped, ...rest } = sheet;
  return rest as Character;
}

/** Combatant → file entry. */
export function combatantToFile(c: Combatant, opts: RosterExportOptions): CombatantFileEntry {
  const entry: CombatantFileEntry = {
    id: c.id,
    name: c.name,
    kind: c.kind,
    order: c.order,
    // Absent means STRIP, so a caller written before this existed does
    // not start leaking. Every other flag here is required and explicit;
    // this one defaults to the discreet answer on purpose.
    sheet: opts.includeProtagonist ? c.sheet : stripProtagonist(c.sheet),
    overrides: c.overrides ?? {},
    slotOrder: c.slotOrder ?? null,
    loadoutRef: c.loadoutRef ?? null,
  };
  // `overrides` rides along even in a sheet-only export: it is what a
  // statblock IS, not something that happened to them. Dropping it would
  // silently change an imported ork's armour.
  if (opts.includeState) entry.state = c.state;
  if (opts.includeNarrative && c.narrativeLog?.length) entry.narrativeLog = c.narrativeLog;
  return entry;
}

export function rosterToFile(roster: Combatant[], opts: RosterExportOptions): CombatantFileEntry[] {
  return roster.map((c) => combatantToFile(c, opts));
}

/**
 * File entry → Combatant.
 *
 * Returns null for anything that cannot be made into a usable combatant,
 * so one bad entry costs one combatant rather than the whole import.
 */
export function combatantFromFile(raw: unknown, index: number): Combatant | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  try {
    return readCombatant(r, index);
  } catch (err) {
    console.error('[roster] Failed to read combatant from file:', err);
    return null;
  }
}

/** The combatant a file record describes, every missing field defaulted. */
function readCombatant(r: Record<string, unknown>, index: number): Combatant {
  const id = typeof r.id === 'string' && r.id ? r.id : `imported-${index}`;
  // A saved condition is a library fact: a file that carries one on a sheet does not bring it to a live one.
  const sheet: Character = r.sheet ? withoutStanding(normalizeCharacter(r.sheet as Character)) : emptyCharacter(id);
  const fresh = emptyCombatState(sheet);
  return {
    id,
    name: (typeof r.name === 'string' && r.name) || sheet.name || 'Combatant',
    kind: r.kind === 'character' ? 'character' : 'statblock',
    sheet,
    overrides: (r.overrides as Partial<DefenderSheet>) ?? {},
    // A sheet-only file has no state - they arrive at full health, which
    // is the whole point of that export mode. A partial state (an older
    // or hand-edited file) is filled in from the fresh one.
    state: r.state ? { ...fresh, ...(r.state as CombatState) } : fresh,
    ...fileExtras(r, index),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/** The rack order, loadout link, log and place in the order a file may carry. */
function fileExtras(r: Record<string, unknown>, index: number) {
  return {
    slotOrder: sanitizeSlotOrder(r.slotOrder),
    loadoutRef: (r.loadoutRef as LoadoutRef | null) ?? null,
    narrativeLog: Array.isArray(r.narrativeLog) ? (r.narrativeLog as Combatant['narrativeLog']) : [],
    order: typeof r.order === 'number' ? r.order : index,
  };
}

export interface RosterImportResult {
  combatants: Combatant[];
  /** Entries that could not be read at all. */
  skipped: number;
}

export function rosterFromFile(raw: unknown): RosterImportResult {
  if (!Array.isArray(raw)) return { combatants: [], skipped: 0 };
  const combatants: Combatant[] = [];
  let skipped = 0;
  raw.forEach((entry, i) => {
    const c = combatantFromFile(entry, i);
    if (c) combatants.push(c);
    else skipped += 1;
  });
  // Ids must be unique - the ammo ledger, defence documents and turn
  // participants are all keyed by them.
  const seen = new Set<string>();
  return {
    combatants: combatants.map((c, i) => {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        return c;
      }
      return { ...c, id: `${c.id}-dup${i}` };
    }),
    skipped,
  };
}

/** Loadouts survive a round trip untouched - they carry no live state. */
export function loadoutsFromFile(raw: unknown): Loadout[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (l): l is Loadout =>
      !!l && typeof l === 'object' && typeof (l as Loadout).name === 'string' && Array.isArray((l as Loadout).weapons),
  );
}

/** True when a parsed file uses the roster format rather than tabs/atkProfiles. */
export function isRosterFile(data: { version?: number; roster?: unknown }): boolean {
  return (data.version ?? 1) >= SESSION_FILE_VERSION && Array.isArray(data.roster);
}
